import React, { useCallback, useEffect, useMemo, useState } from "react";
import API from "../../services/api";

const EMPTY_FORM = { name: "", parent: "", description: "" };

const errorDetail = (error, fallback) => (
    error?.response?.data?.detail
    || error?.response?.data?.name?.[0]
    || fallback
);

const blockedDeleteMessage = (group) => {
    const reasons = [];
    if (group.tag_count > 0) {
        reasons.push(`${group.tag_count} tag${group.tag_count === 1 ? "" : "s"}`);
    }
    if (group.has_children) {
        reasons.push("one or more child groups");
    }
    return reasons.length
        ? `This group contains ${reasons.join(" and ")}. Move them to another group before deleting it.`
        : "";
};

const descendantIds = (groups, rootId) => {
    const children = new Map();
    groups.forEach((group) => {
        const key = group.parent == null ? "" : String(group.parent);
        const rows = children.get(key) || [];
        rows.push(group);
        children.set(key, rows);
    });
    const found = new Set([String(rootId)]);
    const visit = (id) => {
        (children.get(String(id)) || []).forEach((child) => {
            if (!found.has(String(child.id))) {
                found.add(String(child.id));
                visit(child.id);
            }
        });
    };
    visit(rootId);
    return found;
};

const GroupManagement = ({ onChanged }) => {
    const [groups, setGroups] = useState([]);
    const [tags, setTags] = useState([]);
    const [form, setForm] = useState(EMPTY_FORM);
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const [tagQuery, setTagQuery] = useState("");
    const [serverFilter, setServerFilter] = useState("");
    const [connectionFilter, setConnectionFilter] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    const loadGroups = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const response = await API.get("/tag-group-management/");
            setGroups(Array.isArray(response.data?.groups) ? response.data.groups : []);
            setTags(Array.isArray(response.data?.tags) ? response.data.tags : []);
        } catch (loadError) {
            setError(errorDetail(loadError, "Unable to load tag groups."));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadGroups();
    }, [loadGroups]);

    const groupById = useMemo(
        () => new Map(groups.map((group) => [String(group.id), group])),
        [groups]
    );
    const rootGroup = useMemo(
        () => groups.find((group) => group.protected)
            || groups.find((group) => group.parent == null && String(group.name).toLowerCase() === "all devices"),
        [groups]
    );
    const excludedParents = useMemo(
        () => (editingId == null ? new Set() : descendantIds(groups, editingId)),
        [groups, editingId]
    );
    const serverOptions = useMemo(
        () => [...new Set(tags.map((tag) => tag.server__name).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
        [tags]
    );
    const visibleTags = useMemo(() => {
        const query = tagQuery.trim().toLowerCase();
        return tags.filter((tag) => {
            const matchesQuery = !query || String(tag.name || "").toLowerCase().includes(query);
            const matchesServer = !serverFilter || tag.server__name === serverFilter;
            const status = tag.connection_status || "UNKNOWN";
            const matchesConnection = !connectionFilter || status === connectionFilter;
            return matchesQuery && matchesServer && matchesConnection;
        });
    }, [tags, tagQuery, serverFilter, connectionFilter]);

    useEffect(() => {
        if (editingId == null && !form.parent && rootGroup) {
            setForm((current) => ({ ...current, parent: String(rootGroup.id) }));
        }
    }, [editingId, form.parent, rootGroup]);

    const resetForm = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setSelectedTagIds([]);
        setTagQuery("");
        setServerFilter("");
        setConnectionFilter("");
        setError("");
    };

    const update = (field, value) => {
        setForm((current) => ({ ...current, [field]: value }));
    };

    const submit = async (event) => {
        event.preventDefault();
        const name = form.name.trim();
        if (!name) {
            setError("Group name is required.");
            return;
        }
        if (!form.parent) {
            setError("Choose All Devices or another existing parent group.");
            return;
        }
        setSaving(true);
        setError("");
        setMessage("");
        const payload = {
            name,
            parent: Number(form.parent),
            description: form.description.trim(),
        };
        try {
            let savedGroup;
            if (editingId == null) {
                const response = await API.post("/tag-group-management/", payload);
                savedGroup = response.data;
                setMessage("Group created successfully.");
            } else {
                const response = await API.patch(`/tag-group-management/${editingId}/`, payload);
                savedGroup = response.data;
                setMessage("Group updated successfully.");
            }
            if (selectedTagIds.length > 0) {
                await API.post(`/tag-group-management/${savedGroup.id}/tags/`, {
                    tag_ids: selectedTagIds,
                });
                setMessage(`Group saved and ${selectedTagIds.length} tag${selectedTagIds.length === 1 ? "" : "s"} assigned.`);
            }
            resetForm();
            await loadGroups();
            onChanged?.();
        } catch (saveError) {
            setError(errorDetail(saveError, "Unable to save this group."));
        } finally {
            setSaving(false);
        }
    };

    const editGroup = (group) => {
        setEditingId(group.id);
        setForm({
            name: group.name || "",
            parent: group.parent == null && !group.protected && rootGroup
                ? String(rootGroup.id)
                : group.parent == null ? "" : String(group.parent),
            description: group.description || "",
        });
        setSelectedTagIds(tags.filter((tag) => String(tag.group_id) === String(group.id)).map((tag) => tag.id));
        setTagQuery("");
        setMessage("");
        setError("");
    };

    const deleteGroup = async (group) => {
        const blocked = blockedDeleteMessage(group);
        if (blocked) {
            window.alert(blocked);
            return;
        }
        if (!window.confirm(`Delete the empty group “${group.name}”?`)) {
            return;
        }
        setError("");
        setMessage("");
        try {
            await API.delete(`/tag-group-management/${group.id}/`);
            if (String(editingId) === String(group.id)) {
                resetForm();
            }
            setMessage("Group deleted successfully.");
            await loadGroups();
            onChanged?.();
        } catch (deleteError) {
            setError(errorDetail(deleteError, "Unable to delete this group."));
        }
    };

    const toggleTag = (tagId) => {
        setSelectedTagIds((current) => current.includes(tagId)
            ? current.filter((id) => id !== tagId)
            : [...current, tagId]);
    };

    return (
        <>
            <form className="tag-page__group-management-form" onSubmit={submit}>
                <div className="tag-page__field">
                    <label htmlFor="group-management-name">Group name</label>
                    <input
                        id="group-management-name"
                        value={form.name}
                        onChange={(event) => update("name", event.target.value)}
                        maxLength={120}
                        required
                    />
                </div>
                <div className="tag-page__field">
                    <label htmlFor="group-management-parent">Parent group</label>
                    <select
                        id="group-management-parent"
                        value={form.parent}
                        onChange={(event) => update("parent", event.target.value)}
                        required
                    >
                        {groups.filter((group) => !excludedParents.has(String(group.id))).map((group) => (
                            <option key={group.id} value={group.id}>{group.name}</option>
                        ))}
                    </select>
                </div>
                <div className="tag-page__field tag-page__group-description">
                    <label htmlFor="group-management-description">Description</label>
                    <textarea
                        id="group-management-description"
                        value={form.description}
                        onChange={(event) => update("description", event.target.value)}
                        rows={3}
                    />
                </div>
                <fieldset className="tag-page__group-tags">
                    <legend>{editingId == null ? "Assign tags" : "Current and additional tags"}</legend>
                    <div className="tag-page__group-tags-head">
                        <p>
                            Selected tags are moved into this group. A tag already in another group will change groups.
                        </p>
                        <div className="tag-page__group-tag-filters">
                            <input
                                type="search"
                                value={tagQuery}
                                onChange={(event) => setTagQuery(event.target.value)}
                                placeholder="Search tags"
                                aria-label="Search tags to assign"
                            />
                            <select
                                value={serverFilter}
                                onChange={(event) => setServerFilter(event.target.value)}
                                aria-label="Filter assigned tags by server"
                            >
                                <option value="">All servers</option>
                                {serverOptions.map((server) => <option key={server} value={server}>{server}</option>)}
                            </select>
                            <select
                                value={connectionFilter}
                                onChange={(event) => setConnectionFilter(event.target.value)}
                                aria-label="Filter assigned tags by connection"
                            >
                                <option value="">All connections</option>
                                <option value="ONLINE">Online</option>
                                <option value="OFFLINE">Offline</option>
                                <option value="UNKNOWN">Unknown</option>
                            </select>
                        </div>
                    </div>
                    {visibleTags.length === 0 ? (
                        <p className="tag-page__hint">No tags match this search.</p>
                    ) : (
                        <div className="tag-page__group-tag-list">
                            {visibleTags.map((tag) => {
                                const belongsToEditedGroup = editingId != null && String(tag.group_id) === String(editingId);
                                const currentGroup = tag.group_id == null ? "All Devices" : groupById.get(String(tag.group_id))?.name || "Unknown group";
                                const connectionStatus = tag.connection_status || "UNKNOWN";
                                return (
                                    <label key={tag.id} className="tag-page__group-tag-option">
                                        <input
                                            type="checkbox"
                                            checked={selectedTagIds.includes(tag.id)}
                                            onChange={() => toggleTag(tag.id)}
                                            disabled={belongsToEditedGroup}
                                        />
                                        <span>
                                            <strong>{tag.name}</strong>
                                            <small>{tag.server__name || "No server"} · {connectionStatus} · Current: {currentGroup}{tag.archived_at ? " · Archived" : ""}</small>
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                </fieldset>
                <div className="tag-page__actions tag-page__group-actions">
                    <button
                        type="submit"
                        className="tag-page__button tag-page__button--primary"
                        disabled={saving || loading || !rootGroup}
                    >
                        {saving ? "Saving…" : editingId == null ? "Add Group" : "Save Changes"}
                    </button>
                    {editingId != null ? (
                        <button type="button" className="tag-page__button" onClick={resetForm} disabled={saving}>
                            Cancel
                        </button>
                    ) : null}
                </div>
            </form>

            {message ? <p className="tag-page__banner is-success" role="status">{message}</p> : null}
            {error ? <p className="tag-page__banner is-error" role="alert">{error}</p> : null}

            {loading ? (
                <p className="tag-page__empty">Loading groups…</p>
            ) : groups.length === 0 ? (
                <p className="tag-page__empty">No groups have been created.</p>
            ) : (
                <div className="tag-page__table-wrap">
                    <table className="tag-page__table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Parent</th>
                                <th>Tags</th>
                                <th>Child groups</th>
                                <th>Description</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {groups.map((group) => (
                                <tr key={group.id}>
                                    <td className="tag-page__strong">{group.name}</td>
                                    <td>{group.parent == null ? "No parent" : groupById.get(String(group.parent))?.name || `Group ${group.parent}`}</td>
                                    <td>{group.tag_count}</td>
                                    <td>{group.has_children ? "Yes" : "No"}</td>
                                    <td>{group.description || "—"}</td>
                                    <td>
                                        <div className="tag-page__group-row-actions">
                                            <button
                                                type="button"
                                                className="tag-page__button"
                                                onClick={() => editGroup(group)}
                                                disabled={group.protected}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                className="tag-page__button tag-page__button--danger"
                                                onClick={() => deleteGroup(group)}
                                                disabled={group.protected}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
};

export default GroupManagement;
